/**
 * The header's report actions (docs/ux/workspace.md): the persona's two
 * events in the hub's order, the first allowed one as the ink primary, the
 * engine's line under each ("to 7 Awaiting QA" or "Refused:" plus the reason
 * verbatim); a refused event stays focusable and never fires. Preview report
 * and the More menu sit beside them.
 */

import { ExternalLinkIcon, MoreHorizontalIcon } from 'lucide-react';
import type { WorkspaceEventKind, WorkspaceEventOption, WorkspaceView } from '@/app/types';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { EVENT_BUSY, engineLine, eventsFor, primaryEvent } from './describe';
import { exportUrl, previewUrl } from './useWorkspace';

export const OFFLINE_TIP = 'Actions are paused while offline';

export function EventCluster({ view, offline, busy, onEvent, phone }: {
  view: WorkspaceView;
  offline: boolean;
  busy: WorkspaceEventKind | null;
  onEvent: (e: WorkspaceEventOption) => void;
  phone?: boolean;
}) {
  const events = eventsFor(view);
  const primary = primaryEvent(view);
  const complete = view.status === 'qa_complete';
  if (!view.reachable) return null;
  return (
    <div role="group" aria-label="Report actions" className={phone ? 'flex w-full flex-col gap-2' : 'flex flex-wrap items-start gap-2'}>
      {/* QA's events are desktop only (docs/ux/workspace.md, section 4); the page says so in their place. */}
      {!complete && !(phone && view.actorRole === 'qa') ? (
        <div className={phone ? 'flex w-full gap-2' : 'contents'}>
          {events.map((e) => {
            const isPrimary = primary?.kind === e.kind;
            const disabled = offline || busy !== null;
            const button = (
              <Button
                key={e.kind}
                variant={isPrimary ? 'default' : 'outline'}
                data-primary={isPrimary ? 'true' : undefined}
                data-event={e.kind}
                aria-disabled={!e.allowed || disabled ? true : undefined}
                aria-describedby={`engine-${e.kind}`}
                disabled={offline}
                className={phone ? 'h-11 flex-1' : 'h-9'}
                onClick={() => { if (!e.allowed || disabled) return; onEvent(e); }}
              >
                {busy === e.kind ? EVENT_BUSY[e.kind] : e.label}
              </Button>
            );
            return (
              <div key={e.kind} className={phone ? 'flex min-w-0 flex-1 flex-col gap-1' : 'flex flex-col items-stretch gap-1'}>
                {offline ? <Tooltip><TooltipTrigger asChild>{button}</TooltipTrigger><TooltipContent>{OFFLINE_TIP}</TooltipContent></Tooltip> : button}
                <span id={`engine-${e.kind}`} data-slot="engine-line" className={`text-xs ${e.allowed ? 'text-beam-text-muted' : 'text-beam-warning'}`}>{engineLine(e)}</span>
              </div>
            );
          })}
        </div>
      ) : null}
      <div className={phone ? 'flex w-full gap-2' : 'flex gap-2'}>
        <Button variant="outline" asChild className={phone ? 'h-11 flex-1' : 'h-9'}>
          <a href={previewUrl(view.matterRef)} target="_blank" rel="noreferrer">Preview report <ExternalLinkIcon aria-hidden="true" className="size-4" /></a>
        </Button>
        <MoreMenu view={view} phone={phone} />
      </div>
    </div>
  );
}

export function MoreMenu({ view, phone }: { view: WorkspaceView; phone?: boolean }) {
  const complete = view.status === 'qa_complete';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" aria-label="More actions" className={phone ? 'h-11 px-3' : 'h-9 px-3'}>
          <MoreHorizontalIcon aria-hidden="true" className="size-4" />{phone ? null : <span>More</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        <DropdownMenuItem asChild><a href={exportUrl(view.matterRef)} target="_blank" rel="noreferrer">Export working text</a></DropdownMenuItem>
        {complete ? <DropdownMenuItem asChild><a href={`${exportUrl(view.matterRef)}?final=1`} target="_blank" rel="noreferrer">Export final text</a></DropdownMenuItem> : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild><a href={`/matters/${encodeURIComponent(view.matterRef)}?tab=audit`}>Open the audit</a></DropdownMenuItem>
        <DropdownMenuItem asChild><a href={`/matters/${encodeURIComponent(view.matterRef)}?tab=report`}>Open the case hub</a></DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
